import { keycloak } from "@/plugins/auth";
import { defineStore } from "pinia";
import { ref } from "vue";

type Capability =
  | "locations"
  | "location-management"
  | "location-visibility"
  | "trip-planning-ice"
  | "trip-planning-ev"
  | "booking-vehicle"
  | "booking-charger"
  | "chargers"
  | "invite-users";

export const useFeatureStore = defineStore("featureStore", () => {
  // PoC-ONLY — DO NOT PORT AS-IS. `initialiseCapabilities()` below was
  // never actually wired into appStore.bootstrap, so `capabilities` stayed
  // empty for the whole session and every `can()` check silently returned
  // false. Since these capabilities are a dev-only stub anyway (see the
  // "filler for dev only" comment below), the fix here is just to seed the
  // fleet-wide ones directly rather than depend on an init call - the real
  // app will replace this whole store with actual feature-flag/fleet-config
  // data, not just wire up the call this was missing.
  const capabilities = ref<Capability[]>([
    "locations",
    "location-management",
    "trip-planning-ice",
    // "location-visibility" is deliberately NOT granted here. `locations`
    // has no `visibility` column at all (see savedLocation.ts's fromDTO),
    // so the private/fleet picker in FavLocationForm.vue is a
    // ported-over-but-currently-inert concept for this PoC - gating it on
    // an ungranted capability hides it now without deleting the
    // field/UI, so it's ready to re-enable once the backend and this
    // domain's visibility concept are reintegrated with the main app.
  ]);

  async function initialiseCapabilities() {
    // TODO: figure out how fetch feature flags and fleet config once these are created

    // WARNING: this is a filler for dev only
    const tempCaptureArray: Capability[] = [];

    // user stuff
    const hasSubscriberRole = keycloak.tokenParsed?.realm_access?.roles?.includes('subscribed') ?? false
    if (hasSubscriberRole) tempCaptureArray.push('invite-users')

    // all the stuff in the middle
    tempCaptureArray.push(
      "locations",
      "location-management",
      "trip-planning-ice",
    );
    capabilities.value = tempCaptureArray;
  }

  function can(capability: Capability): boolean {
    return capabilities.value.includes(capability);
  }

  return {
    can,
    initialiseCapabilities,
  };
});
